import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Stethoscope, FileText, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  ChevronRight, Copy, Check, Circle, ShieldCheck,
} from 'lucide-react';
import { instances as instancesApi, type TlsDiagnose, type TlsBefund, type TlsSicherstellenErgebnis } from '../api';

/**
 * Wenn etwas nicht erreichbar ist: erst fragen, dann lesen.
 *
 * ══ WOGEGEN DAS GEBAUT IST ═════════════════════════════════════════════════
 *
 * Am 29.09.2026 war die ojfm-Sales-Instanz nach einem Deploy 45 Minuten lang
 * immer wieder unerreichbar (SSL-Handshake bricht ab, hinter Cloudflare ein
 * 525). Die Ursache stand in vier verschiedenen Protokollen, und die Suche
 * dauerte eine halbe Stunde: npm-docker-sync legte den Proxy-Eintrag 27 Mal
 * neu an, weil NPM beim nginx-Reload die HTTP-Antwort abschneidet und das
 * Anlegen dadurch als gescheitert gilt. Zwischen „Removing" und „Created"
 * fehlt `listen 443` — und genau dann trifft eine Anfrage ins Leere.
 *
 * Dieses Muster ist entscheidbar: man muss nur zaehlen. Deshalb fragt die
 * Diagnose danach, statt dass jemand Protokolle liest.
 *
 * ══ DIE PROTOKOLLE LAUFEN NICHT MIT ════════════════════════════════════════
 *
 * Ein offener Strom hielte je Zuschauer eine SSH-Verbindung offen, und wer den
 * Reiter vergisst, haelt sie stundenlang. Deshalb auf Knopfdruck: eine
 * Verbindung, ein Abruf, fertig.
 */

const AMPEL_SYMBOL: Record<string, typeof CheckCircle2> = {
  gruen: CheckCircle2,
  gelb: AlertTriangle,
  rot: XCircle,
  unbekannt: Circle,
};

const AMPEL_FARBE: Record<string, string> = {
  gruen: 'text-emerald-600',
  gelb: 'text-amber-600',
  rot: 'text-rose-600',
  unbekannt: 'text-slate-400',
};

/**
 * Ein Schritt der Kette.
 *
 * Aufgeklappt ist, was nicht stimmt — wer die Diagnose aufruft, sucht den
 * Bruch und nicht die Bestaetigung, dass die ersten vier Schritte sassen.
 */
function BefundZeile({ b }: { b: TlsBefund }) {
  const [offen, setOffen] = useState(b.ampel === 'rot');
  const Symbol = AMPEL_SYMBOL[b.ampel] ?? Circle;
  const hatDetails = !!(b.deutung || b.rat || b.ergebnis.includes('\n'));
  const [kopf, ...rest] = b.ergebnis.split('\n');

  return (
    <li className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={() => hatDetails && setOffen((v) => !v)}
        className={`flex w-full items-start gap-2 px-4 py-2 text-left ${
          hatDetails ? 'hover:bg-slate-50' : 'cursor-default'
        }`}
      >
        <Symbol size={13} className={`mt-0.5 shrink-0 ${AMPEL_FARBE[b.ampel]}`} />
        <span className="min-w-0 flex-1">
          <span className="text-xs font-medium text-slate-800">{b.schritt}</span>
          <span className="ml-2 text-[11px] text-slate-500">{kopf}</span>
        </span>
        {hatDetails && (
          <ChevronRight
            size={12}
            className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${
              offen ? 'rotate-90' : ''
            }`}
          />
        )}
      </button>

      {offen && hatDetails && (
        <div className="space-y-1.5 bg-slate-50 px-4 py-2 pl-9">
          {rest.length > 0 && (
            <pre className="overflow-x-auto rounded bg-slate-900 p-2 text-[10px] leading-relaxed text-slate-200">
              {rest.join('\n')}
            </pre>
          )}
          {b.deutung && (
            <p className="whitespace-pre-line text-[11px] leading-relaxed text-slate-700">
              {b.deutung}
            </p>
          )}
          {b.rat && (
            <p className="text-[11px] leading-relaxed text-slate-600">
              <strong className="font-medium">Zu tun:</strong> {b.rat}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export default function DiagnosePanel({ instanceId }: { instanceId: string }) {
  const [gewaehlt, setGewaehlt] = useState('');
  const [zeilen, setZeilen] = useState(300);
  const [kopiert, setKopiert] = useState(false);

  // Die Containerliste geht auch ueber SSH, ist aber billig und wird ohne sie
  // zur Ratestunde: man muesste den Containernamen von Hand tippen.
  const liste = useQuery({
    queryKey: ['instances', instanceId, 'container'],
    queryFn: () => instancesApi.container(instanceId),
    staleTime: 30_000,
  });

  const diagnose = useQuery<TlsDiagnose>({
    queryKey: ['instances', instanceId, 'tls-diagnose'],
    queryFn: () => instancesApi.tlsDiagnose(instanceId),
    // Nur auf Verlangen: der Lauf ruft die Seite von aussen ab und sieht sich
    // dann Proxy, Zertifikat und Container auf dem Wirt an.
    enabled: false,
  });

  const logs = useMutation({
    mutationFn: () => instancesApi.containerLogs(instanceId, gewaehlt, zeilen),
  });

  // Zertifikat beantragen — danach gleich neu pruefen, damit die Ampel zeigt,
  // ob es gegriffen hat.
  const tls = useMutation<TlsSicherstellenErgebnis, Error>({
    mutationFn: () => instancesApi.tlsSicherstellen(instanceId),
    onSuccess: () => { diagnose.refetch(); },
  });

  const container = liste.data?.container ?? [];

  return (
    <div className="space-y-4">
      {/* ── Erreichbarkeit ────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <Stethoscope size={14} /> Erreichbarkeit prüfen
            </h3>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Geht die Kette von aussen bis zum Container durch — und wo reisst sie?
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => tls.mutate()}
              disabled={tls.isPending}
              title="Pflegt der PC den DNS, setzt er zuerst den A-Record auf den Server. Dann beantragt er im Proxy des Servers ein Let's-Encrypt-Zertifikat für jede Adresse und hängt es an. Fasst keine Container an — geht deshalb auch bei beobachteten Instanzen."
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <ShieldCheck size={12} className={tls.isPending ? 'animate-pulse' : ''} />
              {tls.isPending ? 'beantragt …' : 'TLS sicherstellen'}
            </button>
            <button
              type="button"
              onClick={() => diagnose.refetch()}
              disabled={diagnose.isFetching}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              <RefreshCw size={12} className={diagnose.isFetching ? 'animate-spin' : ''} />
              {diagnose.isFetching ? 'prüft …' : 'Prüfen'}
            </button>
          </div>
        </div>

        {(tls.data || tls.error) && (
          <ul className="border-b border-slate-100 px-4 py-2 text-xs">
            {tls.error && <li className="text-rose-700">Zertifikat: {tls.error.message}</li>}
            {tls.data?.ergebnisse.map((e) => (
              <li key={e.adresse} className={e.ok ? 'text-emerald-800' : 'text-rose-700'}>
                <span className="font-medium">{e.adresse}:</span> {e.meldung}
              </li>
            ))}
          </ul>
        )}

        {diagnose.data ? (
          <>
            {/* Nach der schlimmsten Ampel, nicht nach der Bruchstelle: ein
                fehlender Healthcheck ist gelb und kein Bruch — ihn rot zu
                faerben hiesse, den Unterschied einzuebnen, um den es hier
                gerade geht. */}
            <p
              className={`px-4 py-2 text-xs ${
                diagnose.data.befunde.some((b) => b.ampel === 'rot')
                  ? 'bg-rose-50 text-rose-900'
                  : diagnose.data.befunde.some((b) => b.ampel === 'gelb')
                    ? 'bg-amber-50 text-amber-900'
                    : 'bg-emerald-50 text-emerald-900'
              }`}
            >
              {diagnose.data.zusammenfassung}
            </p>
            <ul>
              {diagnose.data.befunde.map((b, i) => (
                <BefundZeile key={`${b.schritt}-${i}`} b={b} />
              ))}
            </ul>
            <p className="border-t border-slate-100 px-4 py-1.5 text-[10px] text-slate-400">
              {diagnose.data.hostname ?? 'ohne Adresse'}
              {diagnose.data.server ? ` auf ${diagnose.data.server}` : ''} ·{' '}
              geprüft um {new Date(diagnose.data.geprueftAm).toLocaleTimeString('de-DE')}
            </p>
          </>
        ) : (
          <p className="px-4 py-4 text-xs leading-relaxed text-slate-500">
            {diagnose.isError
              ? `Die Prüfung ist fehlgeschlagen: ${(diagnose.error as Error).message}`
              : 'Noch nicht geprüft. Der Lauf ruft die Adresse von aussen ab und sieht sich dann '
                + 'Proxy-Eintrag, Zertifikat und Container auf dem Wirt an — das dauert einige '
                + 'Sekunden.'}
          </p>
        )}
      </div>

      {/* ── Protokolle ────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-2.5">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            <FileText size={14} /> Container-Protokoll
          </h3>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Auf Abruf, nicht mitlaufend — ein offener Strom hielte je Zuschauer eine
            SSH-Verbindung offen.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2 px-4 py-2.5">
          <label className="block min-w-[14rem] flex-1">
            <span className="mb-0.5 block text-[10px] text-slate-500">Container</span>
            <select
              value={gewaehlt}
              onChange={(e) => setGewaehlt(e.target.value)}
              disabled={liste.isLoading || container.length === 0}
              className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">
                {liste.isLoading ? 'wird geladen …' : '— wählen —'}
              </option>
              {container.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} — {c.status}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[10px] text-slate-500">Zeilen</span>
            <select
              value={zeilen}
              onChange={(e) => setZeilen(Number(e.target.value))}
              className="rounded border border-slate-300 px-2 py-1.5 text-xs"
            >
              {[100, 300, 1000, 2000].map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => logs.mutate()}
            disabled={!gewaehlt || logs.isPending}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {logs.isPending ? 'holt …' : 'Abrufen'}
          </button>
          {logs.data && (
            <button
              type="button"
              title="Protokoll in die Zwischenablage"
              onClick={() => {
                navigator.clipboard.writeText(logs.data.text);
                setKopiert(true);
                window.setTimeout(() => setKopiert(false), 2000);
              }}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-slate-600 hover:bg-slate-50"
            >
              {kopiert ? <Check size={12} /> : <Copy size={12} />}
            </button>
          )}
        </div>

        {liste.data?.hinweis && (
          <p className="px-4 pb-3 text-[11px] leading-relaxed text-amber-800">
            {liste.data.hinweis}
          </p>
        )}
        {liste.isError && (
          <p className="px-4 pb-3 text-[11px] text-rose-700">
            Die Containerliste liess sich nicht holen: {(liste.error as Error).message}
          </p>
        )}
        {logs.isError && (
          <p className="px-4 pb-3 text-[11px] text-rose-700">{(logs.error as Error).message}</p>
        )}

        {logs.data && (
          <>
            <pre className="max-h-[28rem] overflow-auto border-t border-slate-200 bg-slate-900 p-3 text-[10px] leading-relaxed text-slate-200">
              {logs.data.text}
            </pre>
            <p className="px-4 py-1.5 text-[10px] text-slate-400">
              {logs.data.zeilen} Zeilen
              {logs.data.abgeschnitten && ' — oben abgeschnitten, für mehr Verlauf eine höhere Zeilenzahl wählen'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
